// One session on its detail screen: timeline lanes, spans, corrections, tokens. A port of the Mac's SessionDetail.
import { mergeIntervals, sumIntervals, type AgentPiece, type Interval } from '../../../packages/core/src/index.ts';
import type { ActivitySpan, AgentSession, SpanEdit, UsageTotal } from '../api/types.ts';
import { HOUR } from '../lib/calendar.ts';
import { agentPieces, isQuiet, pillState, providerInfo, spanEnd, type PillState, type RangeData } from './data.ts';
import type { Segment } from './snapshot.ts';

export interface SessionDetail {
  record: AgentSession;
  children: AgentSession[];
  spans: ActivitySpan[];
  childSpans: Map<string, ActivitySpan[]>;
  edits: SpanEdit[];
  usage: UsageSummary;
  range: RangeData;
}

/** The session and its subagent threads out of loaded data (the detail screen loads them with all their spans). */
export function sessionDetail(loaded: RangeData, id: string): SessionDetail | null {
  const record = loaded.sessions.find((s) => s.id === id);
  if (!record) return null;
  const children = loaded.sessions.filter((s) => s.parentId === id);
  const family = [record, ...children];
  const childSpans = new Map<string, ActivitySpan[]>();
  for (const child of children) childSpans.set(child.id, loaded.spans.get(child.id) ?? []);
  const edits = family.flatMap((s) => loaded.edits.get(s.id) ?? []).sort((a, b) => a.createdAt - b.createdAt);
  const usage = usageSummary(family.flatMap((s) => loaded.usage.get(s.id) ?? []));
  const range: RangeData = {
    range: { start: 0, end: Number.MAX_SAFE_INTEGER }, entries: [], sessions: family,
    spans: new Map(family.map((s) => [s.id, loaded.spans.get(s.id) ?? []])),
    edits: new Map(family.map((s) => [s.id, loaded.edits.get(s.id) ?? []])),
    usage: new Map(family.map((s) => [s.id, loaded.usage.get(s.id) ?? []])),
  };
  return { record, children, spans: loaded.spans.get(id) ?? [], childSpans, edits, usage, range };
}

export interface DetailFigures {
  state: PillState;
  isOpen: boolean;
  pieces: AgentPiece[];
  countedMs: number;
  waitingMs: number;
  discardedMs: number;
  movedMs: number;
  timeline: Interval;
  tickHours: number;
}

/** Where an open span ends now (a quiet session stops at its last activity plus grace). */
export function detailSpanEnd(d: SessionDetail, span: ActivitySpan, now: number): number {
  const session = span.sessionId === d.record.id ? d.record : d.children.find((c) => c.id === span.sessionId);
  return spanEnd(session, span, now);
}

export function detailFigures(d: SessionDetail, now: number): DetailFigures {
  const pieces = agentPieces(d.range, now);
  const counted = sumIntervals(mergeIntervals(pieces.map((p) => ({ start: p.start, end: p.end }))));
  const all = [...d.spans, ...[...d.childSpans.values()].flat()];
  const engine = sumIntervals(mergeIntervals(all.filter((s) => s.state === 'working').map((s) => ({ start: s.startAt, end: detailSpanEnd(d, s, now) }))));
  const waitingMs = d.spans.filter((s) => s.state === 'waiting_input' || s.state === 'waiting_permission').reduce((n, s) => n + detailSpanEnd(d, s, now) - s.startAt, 0);
  const isOpen = d.record.state !== 'ended' && d.spans.length > 0 && d.spans[d.spans.length - 1]?.endAt === null;
  const first = d.spans[0]?.startAt ?? d.record.startedAt;
  const ends = d.spans.map((s) => s.endAt).filter((e): e is number => e !== null);
  const last = Math.max(ends.length > 0 ? Math.max(...ends) : first, isOpen ? now : 0, first + 60_000);
  const start = first - (first % HOUR);
  const endRaw = last + HOUR - 1;
  const end = Math.max(start + HOUR, endRaw - (endRaw % HOUR));
  const hours = (end - start) / HOUR;
  const state = d.record.state === 'working' && isQuiet(d.record, now) ? 'idle' : pillState(d.record, now);
  return {
    state, isOpen, pieces, countedMs: counted, waitingMs, discardedMs: Math.max(0, engine - counted),
    movedMs: pieces.filter((p) => p.project !== d.record.projectId).reduce((n, p) => n + p.end - p.start, 0),
    timeline: { start, end },
    tickHours: hours <= 12 ? 1 : hours <= 24 ? 2 : hours <= 72 ? 6 : 24,
  };
}

export interface DetailLane {
  id: string;
  label: string;
  segments: Segment[];
}

export function detailLanes(d: SessionDetail, figures: DetailFigures, now: number, labels: { waitingInput: string; waitingPermission: string; subagent: string }): DetailLane[] {
  const lane = (session: AgentSession, spans: ActivitySpan[], label: string): DetailLane => {
    const counted = mergeIntervals(figures.pieces.filter((p) => p.session === session.id).map((p) => ({ start: p.start, end: p.end })));
    const waiting = spans.filter((s) => s.state === 'waiting_input' || s.state === 'waiting_permission').map((s): Segment => ({
      start: s.startAt, end: detailSpanEnd(d, s, now), kind: 'waiting', label: s.state === 'waiting_permission' ? labels.waitingPermission : labels.waitingInput,
    }));
    const segments = [...counted.map((i): Segment => ({ start: i.start, end: i.end, kind: 'counted' })), ...waiting].sort((a, b) => a.start - b.start);
    return { id: session.id, label, segments };
  };
  return [
    lane(d.record, d.spans, providerInfo(d.record.provider).name),
    ...d.children.map((c) => lane(c, d.childSpans.get(c.id) ?? [], labels.subagent)),
  ];
}

/** The pieces of one working span after corrections (for its row: where the time went). */
export function spanPieces(figures: DetailFigures, span: ActivitySpan, end: number): AgentPiece[] {
  return figures.pieces.filter((p) => p.session === span.sessionId && p.start < end && p.end > span.startAt);
}

// MARK: Tokens

export interface UsageSummary {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  /** Part of `output`. */
  reasoning: number;
  /** US dollars × 1 000 000 as the provider reports it; `null` when it reports none. */
  costMicroUSD: number | null;
  activeCliMs: number | null;
  activeUserMs: number | null;
  sources: Set<string>;
}

export function usageSummary(records: readonly UsageTotal[]): UsageSummary {
  const s: UsageSummary = { input: 0, output: 0, cacheRead: 0, cacheWrite: 0, reasoning: 0, costMicroUSD: null, activeCliMs: null, activeUserMs: null, sources: new Set() };
  for (const r of records) {
    s.sources.add(r.source);
    switch (r.kind) {
      case 'input': s.input += r.value; break;
      case 'output': s.output += r.value; break;
      case 'cache_read': s.cacheRead += r.value; break;
      case 'cache_write': s.cacheWrite += r.value; break;
      case 'reasoning': s.reasoning += r.value; break;
      case 'cost_microusd': s.costMicroUSD = (s.costMicroUSD ?? 0) + r.value; break;
      case 'active_cli_ms': s.activeCliMs = (s.activeCliMs ?? 0) + r.value; break;
      case 'active_user_ms': s.activeUserMs = (s.activeUserMs ?? 0) + r.value; break;
    }
  }
  return s;
}

export const totalTokens = (u: UsageSummary): number => u.input + u.output + u.cacheRead + u.cacheWrite;
export const usageIsEmpty = (u: UsageSummary): boolean => totalTokens(u) === 0 && u.costMicroUSD === null && u.activeCliMs === null;

export function addUsage(a: UsageSummary, b: UsageSummary): UsageSummary {
  const plus = (x: number | null, y: number | null) => (x === null && y === null ? null : (x ?? 0) + (y ?? 0));
  return {
    input: a.input + b.input, output: a.output + b.output, cacheRead: a.cacheRead + b.cacheRead, cacheWrite: a.cacheWrite + b.cacheWrite,
    reasoning: a.reasoning + b.reasoning, costMicroUSD: plus(a.costMicroUSD, b.costMicroUSD), activeCliMs: plus(a.activeCliMs, b.activeCliMs),
    activeUserMs: plus(a.activeUserMs, b.activeUserMs), sources: new Set([...a.sources, ...b.sources]),
  };
}
